import React, { useState } from 'react';
import { Box, Button, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import type { AgentAction } from '../../Services/agents';
import { ActionDialog } from './ActionDialog';
import { severityColor } from './orgHelpers';

interface Props {
	actions: AgentAction[];
	onChange: (actions: AgentAction[]) => void;
}

// #region ActionsEditor
// Les actions par défaut d'un agent, en liste lisible ; le formulaire vit dans une
// modale (ActionDialog). Les modifications ne partent au serveur qu'avec
// « Enregistrer une nouvelle version » du parent.
export const ActionsEditor: React.FC<Props> = ({ actions, onChange }) => {
	const [editing, setEditing] = useState<number | 'new' | null>(null);

	const save = (a: AgentAction) => {
		onChange(editing === 'new' ? [...actions, a] : actions.map((x, i) => (i === editing ? a : x)));
		setEditing(null);
	};
	const remove = (i: number) => onChange(actions.filter((_, j) => j !== i));

	return (
		<Stack spacing={1}>
			{actions.length === 0 && (
				<Typography variant="body2" color="text.secondary">
					Aucune action : cet agent ne surveillera rien tant qu'on ne lui en donne pas.
				</Typography>
			)}
			{actions.map((a, i) => (
				<ActionRow key={a.id || i} action={a} onEdit={() => setEditing(i)} onRemove={() => remove(i)} />
			))}
			<Button startIcon={<AddIcon />} onClick={() => setEditing('new')} sx={{ alignSelf: 'flex-start' }}>
				Ajouter une action
			</Button>
			<ActionDialog
				open={editing !== null}
				action={editing === 'new' || editing === null ? null : actions[editing]}
				existingIds={actions.map((a) => a.id)}
				onClose={() => setEditing(null)}
				onSave={save}
			/>
		</Stack>
	);
};
// #endregion

// #region ActionRow
const ActionRow: React.FC<{ action: AgentAction; onEdit: () => void; onRemove: () => void }> = ({ action, onEdit, onRemove }) => (
	<Paper variant="outlined" sx={{ p: 1.5 }}>
		<Stack direction="row" spacing={1} alignItems="flex-start">
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
					<Typography variant="subtitle2">{action.name}</Typography>
					<Chip size="small" color={severityColor(action.severity)} label={action.severity} />
					<Chip size="small" variant="outlined" label={action.default_enabled ? 'active par défaut' : 'inactive par défaut'} />
					<Typography variant="caption" color="text.secondary">
						<code>{action.id}</code>
					</Typography>
				</Stack>
				<Typography variant="body2" sx={{ mt: 0.5 }}>
					{action.description}
				</Typography>
				<Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
					{Object.entries(action.params || {}).map(([k, v]) => (
						<Chip key={k} size="small" label={`${k} = ${String(v)}`} />
					))}
				</Stack>
			</Box>
			<Tooltip title="Modifier">
				<IconButton size="small" onClick={onEdit}>
					<EditIcon fontSize="small" />
				</IconButton>
			</Tooltip>
			<Tooltip title="Retirer">
				<IconButton size="small" onClick={onRemove}>
					<DeleteIcon fontSize="small" />
				</IconButton>
			</Tooltip>
		</Stack>
	</Paper>
);
// #endregion
